import React, { useState, useEffect } from 'react'
import { motion } from 'framer-motion'
import { useNavigate, Link } from 'react-router-dom'
import * as FiIcons from 'react-icons/fi'
import SafeIcon from '../../common/SafeIcon'
import { useAuth } from '../../context/AuthContext'

const Login = () => {
  const [email, setEmail] = useState('')
  const [password, setPassword] = useState('')
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState(null)
  const navigate = useNavigate()
  const { signIn, user } = useAuth()

  // Redirect if already logged in
  useEffect(() => {
    if (user) {
      console.log('User already logged in, redirecting to home')
      navigate('/')
    }
  }, [user, navigate])

  const handleSubmit = async (e) => {
    e.preventDefault()
    
    if (!email || !password) {
      setError('Please fill in all fields')
      return
    }

    try {
      setLoading(true)
      setError(null)
      console.log('Attempting to sign in...')
      
      await signIn({ email, password })
      console.log('Sign in successful, redirecting...')
      navigate('/')
    } catch (err) {
      console.error('Login error:', err)
      setError(err.message || 'Failed to sign in. Please check your credentials.')
    } finally {
      setLoading(false)
    }
  }

  return (
    <motion.div
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      className="max-w-md mx-auto bg-white rounded-3xl shadow-dreamy p-8"
    >
      <h2 className="text-2xl font-display font-bold text-center text-purple-700 mb-6">
        Welcome Back!
      </h2>

      {error && (
        <div className="bg-red-100 border border-red-400 text-red-700 px-4 py-3 rounded-lg mb-6">
          <p>{error}</p>
        </div>
      )}

      <form onSubmit={handleSubmit}>
        <div className="mb-4">
          <label htmlFor="email" className="block text-purple-700 font-medium mb-2">
            Email
          </label>
          <div className="relative">
            <SafeIcon 
              icon={FiIcons.FiMail} 
              className="absolute left-3 top-1/2 transform -translate-y-1/2 text-purple-500" 
            />
            <input
              type="email"
              id="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              className="w-full pl-10 pr-4 py-2 rounded-xl border border-purple-200 focus:border-purple-400 focus:ring focus:ring-purple-300 focus:ring-opacity-50"
              placeholder="your.email@example.com"
              disabled={loading}
            />
          </div>
        </div>

        <div className="mb-6">
          <label htmlFor="password" className="block text-purple-700 font-medium mb-2">
            Password
          </label>
          <div className="relative">
            <SafeIcon 
              icon={FiIcons.FiLock} 
              className="absolute left-3 top-1/2 transform -translate-y-1/2 text-purple-500" 
            />
            <input
              type="password"
              id="password"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              className="w-full pl-10 pr-4 py-2 rounded-xl border border-purple-200 focus:border-purple-400 focus:ring focus:ring-purple-300 focus:ring-opacity-50"
              placeholder="••••••••"
              disabled={loading}
            />
          </div>
        </div>

        <motion.button
          type="submit"
          whileHover={{ scale: 1.02 }}
          whileTap={{ scale: 0.98 }}
          className="w-full bg-primary-500 hover:bg-primary-600 text-white py-3 px-6 rounded-xl font-medium flex items-center justify-center gap-2"
          disabled={loading}
        >
          {loading ? (
            <>
              <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-white"></div>
              Signing In...
            </>
          ) : (
            <>
              <SafeIcon icon={FiIcons.FiLogIn} className="text-lg" />
              Sign In
            </>
          )}
        </motion.button>

        <div className="mt-6 text-center">
          <p className="text-gray-600">
            Don't have an account?{' '}
            <Link to="/signup" className="text-primary-500 hover:text-primary-700 font-medium">
              Sign up
            </Link>
          </p>
        </div>
      </form>
    </motion.div>
  )
}

export default Login